import { plainToClass } from "class-transformer";
import { SiteReport } from "./sites-tracker.entities";
import { SiteReportDataTable } from "./data-table";
import { forEach } from "lodash";
import { FilterSection, IFilteringParent } from "./sites-tracker.filter";
import { Study } from "../../../../shared/entities/scripts/study";
import { Appendix } from "../../../../shared/entities/scripts/appendix";
import { Site } from "../../../../shared/entities/scripts/site";


export class SitesTrackerPage implements IFilteringParent {
    trials: SiteReport[];
    sites: SiteReport[] = [];
    platforms: SiteReport[] = [];
    dataTable: SiteReportDataTable;
    dataTables: SiteReportDataTable[] = [];
    siteNames: string[] = [];
    siteCodes: string[] = [];
    filter: FilterSection;
    studyIdMap: Map<string, number> = new Map<string, number>();
    allSites: Site[] = [];
    allStudies: Study[] = [];
    allAppendices: Appendix[] = [];
    siteNameMap: Map<number, string> = new Map<number, string>();
    trialNameMap: Map<string, number> = new Map<string, number>();


    constructor(trialDataJson: any, lastUpdated: string) {

        this.studyIdMap.set("VI", 2);
        this.studyIdMap.set("CD", 3);
        this.studyIdMap.set("SD", 4);
        this.studyIdMap.set("AD", 5);
        this.studyIdMap.set("EI", 6);

        this.trialNameMap.set("Vital Trial A", 1);
        this.trialNameMap.set("tDCS & BrainHQ", 3);
        this.trialNameMap.set("Modafinil/Solriamfetol", 5);
        this.trialNameMap.set("Melatonin/Light", 6);
        this.trialNameMap.set("IVIG", 8);
        this.trialNameMap.set("Ivabradine", 9);
        this.trialNameMap.set("Cardiopulmonary Rehab", 11);
        this.trialNameMap.set("Structured Pacing", 12);

        this.trials = plainToClass(SiteReport, <SiteReport[]>trialDataJson);
        this.trials.forEach((trial: SiteReport) => {
            trial.NeedsPercentCalculation = false;
            trial.Initialize();
            if (!this.siteNameMap.has(parseInt(trial.SiteCode.substr(2, 3)))) {
                this.siteNameMap.set(parseInt(trial.SiteCode.substr(2, 3)), trial.SiteName);
            }
            if(!this.siteNames.includes(trial.SiteName)){
                this.siteNames.push(trial.SiteName);
            }
            if (!this.siteCodes.includes(trial.SiteCode)) {
                this.siteCodes.push(trial.SiteCode);
            }
        })

        this.dataTable = new SiteReportDataTable(this.trials, "trial");
        this.dataTables.push(this.dataTable);

        forEach(this.siteCodes, (siteCode: string) => {
            let tempSite: SiteReport = new SiteReport();
            let siteToCopy: SiteReport = this.trials.filter((trial: SiteReport) => trial.SiteCode == siteCode)[0];
            tempSite.TrialName = "All Trials";
            tempSite.SiteName = siteToCopy.SiteName;
            tempSite.SiteCode = siteCode;
            tempSite.PlatformName = siteToCopy.PlatformName;
            tempSite.MonthsSinceActivation = siteToCopy.MonthsSinceActivation;
            tempSite.TrialRandomizedCount = this.trials.filter((trial: SiteReport) => trial.SiteCode == siteCode).reduce((sum, trial) => sum + trial.TrialRandomizedCount, 0);
            let filteredSites = this.trials.filter((trial: SiteReport) => trial.SiteCode == siteCode);

            let sum = 0;
            forEach(filteredSites, (site: SiteReport) => {
                if (site.DaysSinceLastRandomizationTrial == -1) {
                    sum = sum;
                }
                else {
                    sum += site.DaysSinceLastRandomizationTrial;
                }
            });

            tempSite.DaysSinceLastRandomizationTrial = sum / (filteredSites.length == 0 ? 1 : filteredSites.length);
            tempSite.EnrolledLast30Days = this.trials.filter((trial: SiteReport) => trial.SiteCode == siteCode).reduce((sum, trial) => sum + trial.EnrolledLast30Days, 0);
            tempSite.DaysFromInitatedToExecuted = siteToCopy.DaysFromInitatedToExecuted;
            tempSite.DaysFromRegSentToRegComplete = siteToCopy.DaysFromRegSentToRegComplete;
            tempSite.DaysFromActivationToRandomization = siteToCopy.DaysFromActivationToRandomization;
            tempSite.TotalPartialStudyInverventionCompleters = this.trials.filter((trial: SiteReport) => trial.SiteCode == siteCode).reduce((sum, trial) => sum + trial.TotalPartialStudyInverventionCompleters, 0);
            tempSite.TotalDiscontinuedIntervention = this.trials.filter((trial: SiteReport) => trial.SiteCode == siteCode).reduce((sum, trial) => sum + trial.TotalDiscontinuedIntervention, 0);
            tempSite.TotalDiscontinuedStudy = this.trials.filter((trial: SiteReport) => trial.SiteCode == siteCode).reduce((sum, trial) => sum + trial.TotalDiscontinuedStudy, 0);
            tempSite.TotalHispanic = this.trials.filter((trial: SiteReport) => trial.SiteCode == siteCode).reduce((sum, trial) => sum + trial.TotalHispanic, 0);
            tempSite.TotalNonWhite = this.trials.filter((trial: SiteReport) => trial.SiteCode == siteCode).reduce((sum, trial) => sum + trial.TotalNonWhite, 0);
            tempSite.TotalHispanicOrNonWhite = this.trials.filter((trial: SiteReport) => trial.SiteCode == siteCode).reduce((sum, trial) => sum + trial.TotalHispanicOrNonWhite, 0);
            tempSite.TotalFemale = this.trials.filter((trial: SiteReport) => trial.SiteCode == siteCode).reduce((sum, trial) => sum + trial.TotalFemale, 0);
            tempSite.TotalVisitsNotDone = this.trials.filter((trial: SiteReport) => trial.SiteCode == siteCode).reduce((sum, trial) => sum + trial.TotalVisitsNotDone, 0);
            tempSite.TotalVisitsCompletedOutOfWindow = this.trials.filter((trial: SiteReport) => trial.SiteCode == siteCode).reduce((sum, trial) => sum + trial.TotalVisitsCompletedOutOfWindow, 0);
            tempSite.TotalVisits = this.trials.filter((trial: SiteReport) => trial.SiteCode == siteCode).reduce((sum, trial) => sum + trial.TotalVisits, 0);
            tempSite.TotalNotEvaluable = this.trials.filter((trial: SiteReport) => trial.SiteCode == siteCode).reduce((sum, trial) => sum + trial.TotalNotEvaluable, 0);
            tempSite.NeedsPercentCalculation = true;
            tempSite.Initialize();

            this.platforms.push(tempSite);
        });

        forEach(this.siteNames, (siteName: string) => {
            let tempSite: SiteReport = new SiteReport();
            let trials = this.trials.filter((trial: SiteReport) => trial.SiteName == siteName);
            let siteToCopy: SiteReport = trials[0];
            const sumRandomized = trials.reduce((accumulator, currentItem) => {
                return accumulator + currentItem.TrialRandomizedCount;
            }, 0);
            tempSite.TrialName = "All Trials";
            tempSite.SiteName = siteName;
            tempSite.SiteCode = siteToCopy.SiteCode.slice(2);
            tempSite.PlatformName = "All Platforms";
            tempSite.MonthsSinceActivation = (this.trials.filter((trial: SiteReport) => trial.SiteName == siteName).reduce((sum, trial) => sum + parseFloat(trial.MonthsSinceActivation), 0)).toFixed(2);
            tempSite.TrialRandomizedCount = sumRandomized;

            let filteredSites = this.trials.filter((trial: SiteReport) => trial.SiteName == siteName);
            let sum = 0;
            forEach(filteredSites, (site: SiteReport) => {
                if (site.DaysSinceLastRandomizationTrial == -1) {
                    sum = sum;
                }
                else {
                    sum+= site.DaysSinceLastRandomizationTrial;
                }
            });

            tempSite.DaysSinceLastRandomizationTrial = sum/ (filteredSites.length == 0 ? 1 : filteredSites.length);

            tempSite.EnrolledLast30Days = this.trials.filter((trial: SiteReport) => trial.SiteName == siteName).reduce((sum, trial) => sum + trial.EnrolledLast30Days, 0);
            tempSite.DaysFromInitatedToExecuted = Math.round(filteredSites.reduce((sum, site) => sum + site.DaysFromInitatedToExecuted, 0) / (filteredSites.length == 0 ? 1 : filteredSites.length) * 100) / 100;
            tempSite.DaysFromRegSentToRegComplete = Math.round(filteredSites.reduce((sum, site) => sum + site.DaysFromRegSentToRegComplete, 0) / (filteredSites.length == 0 ? 1 : filteredSites.length) * 100) / 100;
            tempSite.DaysFromActivationToRandomization = Math.round(filteredSites.reduce((sum, site) => sum + site.DaysFromActivationToRandomization, 0) / (filteredSites.length == 0 ? 1 : filteredSites.length)*100)/100;
            tempSite.TotalPartialStudyInverventionCompleters = this.trials.filter((trial: SiteReport) => trial.SiteName == siteName).reduce((sum, trial) => sum + trial.TotalPartialStudyInverventionCompleters, 0);
            tempSite.TotalDiscontinuedIntervention = this.trials.filter((trial: SiteReport) => trial.SiteName == siteName).reduce((sum, trial) => sum + trial.TotalDiscontinuedIntervention, 0);
            tempSite.TotalDiscontinuedStudy = this.trials.filter((trial: SiteReport) => trial.SiteName == siteName).reduce((sum, trial) => sum + trial.TotalDiscontinuedStudy, 0);
            tempSite.TotalHispanic = this.trials.filter((trial: SiteReport) => trial.SiteName == siteName).reduce((sum, trial) => sum + trial.TotalHispanic, 0);
            tempSite.TotalNonWhite = this.trials.filter((trial: SiteReport) => trial.SiteName == siteName).reduce((sum, trial) => sum + trial.TotalNonWhite, 0);
            tempSite.TotalHispanicOrNonWhite = this.trials.filter((trial: SiteReport) => trial.SiteName == siteName).reduce((sum, trial) => sum + trial.TotalHispanicOrNonWhite, 0);
            tempSite.TotalFemale = this.trials.filter((trial: SiteReport) => trial.SiteName == siteName).reduce((sum, trial) => sum + trial.TotalFemale, 0);
            tempSite.TotalVisitsNotDone = this.trials.filter((trial: SiteReport) => trial.SiteName == siteName).reduce((sum, trial) => sum + trial.TotalVisitsNotDone, 0);
            tempSite.TotalVisitsCompletedOutOfWindow = this.trials.filter((trial: SiteReport) => trial.SiteName == siteName).reduce((sum, trial) => sum + trial.TotalVisitsCompletedOutOfWindow, 0);
            tempSite.TotalVisits = this.trials.filter((trial: SiteReport) => trial.SiteName == siteName).reduce((sum, trial) => sum + trial.TotalVisits, 0);
            tempSite.TotalNotEvaluable = this.trials.filter((trial: SiteReport) => trial.SiteName == siteName).reduce((sum, trial) => sum + trial.TotalNotEvaluable, 0);
            tempSite.NeedsPercentCalculation = true;
            tempSite.Initialize();

            this.sites.push(tempSite);
        });

        this.dataTable = new SiteReportDataTable(this.sites, "site");
        this.dataTables.push(this.dataTable);
        this.dataTable = new SiteReportDataTable(this.platforms, "platform");
        this.dataTables.push(this.dataTable);
        this.setupTabWatcher();
        this.initializeFilters();
        $('#last-update-date').text(lastUpdated);
    }

    initializeFilters(): void {
        let foundSiteIds: string[] = [];
        let foundStudyIds: number[] = [];
        let foundAppendixIds: number[] = [];

        this.trials.forEach((t: SiteReport) => {

            let studyId = this.studyIdMap.get(t.SiteCode.substr(0, 2));
            if (!foundSiteIds.includes(t.SiteCode)) { foundSiteIds.push(t.SiteCode); }
            if (!foundStudyIds.includes(studyId)) {
                this.allStudies.push(new Study(studyId, Study.GetStudyName(studyId)));
                foundStudyIds.push(studyId);
            }
            let appendixId = this.trialNameMap.get(t.TrialName);
            if (!foundAppendixIds.includes(appendixId)) {
                this.allAppendices.push(new Appendix(appendixId, studyId, Appendix.GetAppendixName(appendixId)));
                foundAppendixIds.push(appendixId);
            }
        });

        let foundSiteMap: Map<number, Site> = new Map<number, Site>();
        forEach(this.siteCodes, (siteCode: string) => {
            let siteNumber: number = parseInt(siteCode.substr(2, 3));
            let studyId: number = this.studyIdMap.get(siteCode.substr(0, 2));
            if (foundSiteIds.includes(siteCode)) {
                if (foundSiteMap.has(siteNumber)) {
                    foundSiteMap.get(siteNumber).StudyIds.push(studyId);
                }
                else {
                    let newSite: Site = new Site(siteNumber, this.siteNameMap.get(siteNumber));
                    newSite.StudyIds.push(studyId);
                    foundSiteMap.set(siteNumber, newSite);
                }
            }
        });

        this.allSites = Array.from(foundSiteMap.values()).sort((a: Site, b: Site) => a.Name.localeCompare(b.Name));

        this.filter = new FilterSection(this.allStudies, this.allAppendices, this.allSites, this);
    }

    resetFilters(): void {
        this.filter.resetFilters();
    }

    FilterUpdated() {
        if (this.filter === undefined) {
            return
        }
        let studyIds = this.filter.selectedStudyIds;
        let appendixIds = this.filter.selectedAppendixIds;
        let siteIds = this.filter.selectedSiteIds;
        let siteCodes: string[] = [];
        let siteNames: string[] = [];

        let filteredTrials: SiteReport[] = this.trials.filter((trial: SiteReport) => {
            return studyIds.includes(this.studyIdMap.get(trial.SiteCode.substr(0, 2))) && appendixIds.includes(this.trialNameMap.get(trial.TrialName)) && siteIds.includes(parseInt(trial.SiteCode.substr(2, 3)));
        });

        forEach(filteredTrials, (trial: SiteReport) => {
            if (!siteCodes.includes(trial.SiteCode)) {
                siteCodes.push(trial.SiteCode);
            }
            if (!siteNames.includes(trial.SiteName)) {
                siteNames.push(trial.SiteName);
            }
        });

        this.dataTables[0].dataTable.clear();
        this.dataTables[0].dataTable.rows.add(filteredTrials).draw();

        this.updatePlatformTable(filteredTrials, siteCodes);
        this.updateSiteTable(filteredTrials, siteNames);
    }

    setupTabWatcher(): void {
        $('a[data-toggle="pill"]').on('shown.bs.tab', (e) => {
            if (e.currentTarget.id == "content-trial-tab") {
                this.dataTables.forEach((dataTable: SiteReportDataTable) => {
                    dataTable.refresh();
                })
            }
            else if (e.currentTarget.id == "content-site-tab") {
                this.dataTables.forEach((dataTable: SiteReportDataTable) => {
                    dataTable.refresh();
                })
            }
            else if (e.currentTarget.id == "content-platform-tab") {
                this.dataTables.forEach((dataTable: SiteReportDataTable) => {
                    dataTable.refresh();
                })
            }
        });
    }

    updatePlatformTable(filteredTrials: SiteReport[], siteCodes: string[]): void {
        let filteredPlatformEntries: SiteReport[] = [];


        forEach(siteCodes, (siteCode: string) => {
            let tempSite: SiteReport = new SiteReport();
            let siteToCopy: SiteReport = filteredTrials.filter((trial: SiteReport) => trial.SiteCode == siteCode)[0];
            tempSite.TrialName = "All Trials";
            tempSite.SiteName = siteToCopy.SiteName;
            tempSite.SiteCode = siteCode;
            tempSite.PlatformName = siteToCopy.PlatformName;
            tempSite.MonthsSinceActivation = siteToCopy.MonthsSinceActivation;
            tempSite.TrialRandomizedCount = filteredTrials.filter((trial: SiteReport) => trial.SiteCode == siteCode).reduce((sum, trial) => sum + trial.TrialRandomizedCount, 0);

            let filteredSites = filteredTrials.filter((trial: SiteReport) => trial.SiteCode == siteCode);

            let sum = 0;
            forEach(filteredSites, (site: SiteReport) => {
                if (site.DaysSinceLastRandomizationTrial == -1) {
                    sum = sum;
                }
                else {
                    sum += site.DaysSinceLastRandomizationTrial;
                }
            });

            tempSite.DaysSinceLastRandomizationTrial = sum / (filteredSites.length == 0 ? 1 : filteredSites.length);
            tempSite.EnrolledLast30Days = filteredTrials.filter((trial: SiteReport) => trial.SiteCode == siteCode).reduce((sum, trial) => sum + trial.EnrolledLast30Days, 0);
            tempSite.DaysFromInitatedToExecuted = siteToCopy.DaysFromInitatedToExecuted;
            tempSite.DaysFromRegSentToRegComplete = siteToCopy.DaysFromRegSentToRegComplete;
            tempSite.DaysFromActivationToRandomization = siteToCopy.DaysFromActivationToRandomization;
            tempSite.TotalPartialStudyInverventionCompleters = filteredTrials.filter((trial: SiteReport) => trial.SiteCode == siteCode).reduce((sum, trial) => sum + trial.TotalPartialStudyInverventionCompleters, 0);
            tempSite.TotalDiscontinuedIntervention = filteredTrials.filter((trial: SiteReport) => trial.SiteCode == siteCode).reduce((sum, trial) => sum + trial.TotalDiscontinuedIntervention, 0);
            tempSite.TotalDiscontinuedStudy = filteredTrials.filter((trial: SiteReport) => trial.SiteCode == siteCode).reduce((sum, trial) => sum + trial.TotalDiscontinuedStudy, 0);
            tempSite.TotalHispanic = filteredTrials.filter((trial: SiteReport) => trial.SiteCode == siteCode).reduce((sum, trial) => sum + trial.TotalHispanic, 0);
            tempSite.TotalNonWhite = filteredTrials.filter((trial: SiteReport) => trial.SiteCode == siteCode).reduce((sum, trial) => sum + trial.TotalNonWhite, 0);
            tempSite.TotalHispanicOrNonWhite = filteredTrials.filter((trial: SiteReport) => trial.SiteCode == siteCode).reduce((sum, trial) => sum + trial.TotalHispanicOrNonWhite, 0);
            tempSite.TotalFemale = filteredTrials.filter((trial: SiteReport) => trial.SiteCode == siteCode).reduce((sum, trial) => sum + trial.TotalFemale, 0);
            tempSite.TotalVisitsNotDone = filteredTrials.filter((trial: SiteReport) => trial.SiteCode == siteCode).reduce((sum, trial) => sum + trial.TotalVisitsNotDone, 0);
            tempSite.TotalVisitsCompletedOutOfWindow = filteredTrials.filter((trial: SiteReport) => trial.SiteCode == siteCode).reduce((sum, trial) => sum + trial.TotalVisitsCompletedOutOfWindow, 0);
            tempSite.TotalVisits = filteredTrials.filter((trial: SiteReport) => trial.SiteCode == siteCode).reduce((sum, trial) => sum + trial.TotalVisits, 0);
            tempSite.TotalNotEvaluable = this.trials.filter((trial: SiteReport) => trial.SiteCode == siteCode).reduce((sum, trial) => sum + trial.TotalNotEvaluable, 0);
            tempSite.NeedsPercentCalculation = true;
            tempSite.Initialize();

            filteredPlatformEntries.push(tempSite);
        });

        this.dataTables[2].dataTable.clear();
        this.dataTables[2].dataTable.rows.add(filteredPlatformEntries).draw();
    }

    updateSiteTable(filteredTrials: SiteReport[], siteNames: string[]): void {
        let filteredSiteEntries: SiteReport[] = [];
        forEach(siteNames, (siteName: string) => {
            let tempSite: SiteReport = new SiteReport();
            let trials = this.trials.filter((trial: SiteReport) => trial.SiteName == siteName);
            let siteToCopy: SiteReport = trials[0];
            const sumRandomized = trials.reduce((accumulator, currentItem) => {
                return accumulator + currentItem.TrialRandomizedCount;
            }, 0);
            tempSite.TrialName = "All Trials";
            tempSite.SiteName = siteName;
            tempSite.SiteCode = siteToCopy.SiteCode.slice(2);
            tempSite.PlatformName = "All Platforms";
            tempSite.MonthsSinceActivation = (filteredTrials.filter((trial: SiteReport) => trial.SiteName == siteName).reduce((sum, trial) => sum + parseFloat(trial.MonthsSinceActivation), 0)).toFixed(2);
            tempSite.TrialRandomizedCount = sumRandomized;

            let filteredSites = filteredTrials.filter((trial: SiteReport) => trial.SiteName == siteName);
            let sum = 0;
            forEach(filteredSites, (site: SiteReport) => {
                if (site.DaysSinceLastRandomizationTrial == -1) {
                    sum = sum;
                }
                else {
                    sum+= site.DaysSinceLastRandomizationTrial;
                }
            });

            tempSite.DaysSinceLastRandomizationTrial = sum/ (filteredSites.length == 0 ? 1 : filteredSites.length);
            tempSite.EnrolledLast30Days = filteredTrials.filter((trial: SiteReport) => trial.SiteName == siteName).reduce((sum, trial) => sum + trial.EnrolledLast30Days, 0);
            tempSite.DaysFromInitatedToExecuted = Math.round(filteredSites.reduce((sum, site) => sum + site.DaysFromInitatedToExecuted, 0) / (filteredSites.length == 0 ? 1 : filteredSites.length) * 100) / 100;
            tempSite.DaysFromRegSentToRegComplete = Math.round(filteredSites.reduce((sum, site) => sum + site.DaysFromRegSentToRegComplete, 0) / (filteredSites.length == 0 ? 1 : filteredSites.length) * 100) / 100;
            tempSite.DaysFromActivationToRandomization = Math.round(filteredSites.reduce((sum, site) => sum + site.DaysFromActivationToRandomization, 0) / (filteredSites.length == 0 ? 1 : filteredSites.length) * 100) / 100;
            tempSite.TotalPartialStudyInverventionCompleters = filteredTrials.filter((trial: SiteReport) => trial.SiteName == siteName).reduce((sum, trial) => sum + trial.TotalPartialStudyInverventionCompleters, 0);
            tempSite.TotalDiscontinuedIntervention = filteredTrials.filter((trial: SiteReport) => trial.SiteName == siteName).reduce((sum, trial) => sum + trial.TotalDiscontinuedIntervention, 0);
            tempSite.TotalDiscontinuedStudy = filteredTrials.filter((trial: SiteReport) => trial.SiteName == siteName).reduce((sum, trial) => sum + trial.TotalDiscontinuedStudy, 0);
            tempSite.TotalHispanic = filteredTrials.filter((trial: SiteReport) => trial.SiteName == siteName).reduce((sum, trial) => sum + trial.TotalHispanic, 0);
            tempSite.TotalNonWhite = filteredTrials.filter((trial: SiteReport) => trial.SiteName == siteName).reduce((sum, trial) => sum + trial.TotalNonWhite, 0);
            tempSite.TotalHispanicOrNonWhite = filteredTrials.filter((trial: SiteReport) => trial.SiteName == siteName).reduce((sum, trial) => sum + trial.TotalHispanicOrNonWhite, 0);
            tempSite.TotalFemale = filteredTrials.filter((trial: SiteReport) => trial.SiteName == siteName).reduce((sum, trial) => sum + trial.TotalFemale, 0);
            tempSite.TotalVisitsNotDone = filteredTrials.filter((trial: SiteReport) => trial.SiteName == siteName).reduce((sum, trial) => sum + trial.TotalVisitsNotDone, 0);
            tempSite.TotalVisitsCompletedOutOfWindow = filteredTrials.filter((trial: SiteReport) => trial.SiteName == siteName).reduce((sum, trial) => sum + trial.TotalVisitsCompletedOutOfWindow, 0);
            tempSite.TotalVisits = filteredTrials.filter((trial: SiteReport) => trial.SiteName == siteName).reduce((sum, trial) => sum + trial.TotalVisits, 0);
            tempSite.TotalNotEvaluable = this.trials.filter((trial: SiteReport) => trial.SiteName == siteName).reduce((sum, trial) => sum + trial.TotalNotEvaluable, 0);
            tempSite.NeedsPercentCalculation = true;
            tempSite.Initialize();

            filteredSiteEntries.push(tempSite);
        });
        this.dataTables[1].dataTable.clear();
        this.dataTables[1].dataTable.rows.add(filteredSiteEntries).draw();
    }
}